<template>
  <div class="map-viewer-wrapper" :style="{ height: `calc(100vh - ${topOffset}px)` }">
    <map-viewer-map v-if="ship" ref="mapView" SelectedMap="default" :ships="mapShips"
      :inset-left="insetLeftPx" :inset-bottom="sheetHeightPx"
      :Options="mapOptions" class="map-viewer-canvas" />
    <map-viewer-sidebar
      :class="{ 'panel-closed': !panelOpen }"
      :legend-label="legendLabel"
      :isLoading="isLoading"
      :appliedMassId="appliedMassId"
      :isFilterDirty="isFilterDirty"
      @applyFilter="applyFilter"
      @fly-to="flyTo"
      @legend-hover="onLegendHover"
      @toggleMenu="toggleMenu"
    />
    <button
      type="button"
      class="panel-toggle mz-map-btn"
      :class="{ 'panel-toggle-shifted': panelOpen && isWide }"
      @click="panelOpen = !panelOpen">
      {{ panelOpen ? 'Hide Panel' : 'Show Panel' }}
    </button>
    <map-viewer-menu
      v-if="!isLoading && sheetLevel !== 'closed'"
      :level="sheetLevel"
      :inset-left="insetLeftPx"
      @set-sheet="setSheet" />
  </div>
</template>

<script>
import moment from 'moment';
import Sidebar from '@/views/map_viewer/Sidebar';
import Map from '@/views/map/index';
import Menu from '@/views/map_viewer/Menu';
import { initAssetSummary, applyAssetSummaryFilter } from '@/util/summary/asset-summary-loader';
import filterDeepLink from '@/mixins/filterDeepLink';
import { kmhToKnot } from '@/util/units';
import {
  SPEED_COLORS, speedBuckets, bucketColors, speedBounds, bucketLabel,
} from '@/util/trail/trailColor';

export default {
  name: 'MapViewer',
  mixins: [filterDeepLink],
  filterUrlGroups: ['asset', 'date'],
  components: { MapViewerSidebar: Sidebar, MapViewerMap: Map, MapViewerMenu: Menu },
  data() {
    return {
      isLoading: true,
      ship: null,
      sheetLevel: 'closed',
      panelOpen: window.innerWidth >= 768,
      isWide: window.innerWidth >= 768,
      // Navbar + bar tab Report; diukur, bukan dipatok.
      topOffset: 70,
      legendLabel: '',
      appliedDateRange: null,
      appliedMassId: null,
      loadSeq: 0,
    };
  },
  async mounted() {
    const date = this.$store.getters['filter/date'];
    const initResult = await initAssetSummary(this.$store, {
      dateRange: date,
      allowMinute: false,
      withLastData: true,
      withDevices: true,
      summarySource: 'rollup',
      // Menu.vue baca currAssetSummary.series buat tabel + 4 chart.
      withSeries: true,
      // Tanpa ini agregat khusus tabel datalog gak ditarik dan kolomnya jadi "-".
      seriesVariant: 'datalog',
    });

    this.ship = initResult.assets.find((asset) => asset.massId === initResult.currentMassId);
    this.appliedDateRange = {
      start: new Date(date.start.valueOf()),
      end: new Date(date.end.valueOf()),
    };
    this.appliedMassId = initResult.currentMassId;
    this.isLoading = false;
    this.syncFilterUrl();

    this._onResize = () => {
      this.isWide = window.innerWidth >= 768;
      this.measureTopOffset();
    };
    this.measureTopOffset();
    window.addEventListener('resize', this._onResize);

    const savedView = this.readViewFromUrl();
    await this.$nextTick();
    const svc = this.$refs.mapView && this.$refs.mapView.mapService;
    if (!svc) return;
    if (savedView) svc.initialView = savedView;
    svc.onMapMoved = this.writeViewToUrl;
  },
  computed: {
    isFilterDirty() {
      if (!this.appliedDateRange) return false;
      const { start, end } = this.$store.getters['filter/date'];
      const currStart = start.valueOf();
      const currEnd = end.valueOf();
      const appliedStart = this.appliedDateRange.start.valueOf();
      const appliedEnd = this.appliedDateRange.end.valueOf();

      return (
        currStart !== appliedStart
        || currEnd !== appliedEnd
        || (this.appliedMassId || null) !== (this.$store.getters['filter/currentMassId'] || null)
      );
    },
    mapTrailRange() {
      const r = this.appliedDateRange;
      if (!r?.start || !r?.end) return undefined;
      return { start: moment(r.start).unix(), end: moment(r.end).unix() };
    },
    // `ship` cuma snapshot; peta butuh versi store biar coordinates hasil
    // fetchShipTrail kebaca.
    mapShips() {
      if (!this.ship) return [];
      const fromStore = this.$store.getters['asset/assets']
        .find((a) => String(a.massId) === String(this.ship.massId));
      return [fromStore || this.ship];
    },
    mapOptions() {
      return {
        enableTrail: true,
        disableSummaryFetch: true,
        hideVoyageButton: true,
        trailRange: this.mapTrailRange,
        trailColorBy: 'speed',
      };
    },
    // Di bawah 768px panel nutupin peta, bukan mendampingi.
    insetLeftPx() {
      return this.panelOpen && this.isWide ? 420 : 0;
    },
    // Harus sama persis sama sheetHeight di Menu.vue.
    sheetHeightPx() {
      if (this.sheetLevel === 'small') return this.isWide ? 375 : Math.round(window.innerHeight * 0.45);
      if (this.sheetLevel === 'medium') return Math.round(window.innerHeight * (this.isWide ? 0.55 : 0.65));
      if (this.sheetLevel === 'large') return Math.round(window.innerHeight * (this.isWide ? 0.7 : 0.85));
      return 0;
    },
    trailPoints() {
      const svc = this.$refs.mapView && this.$refs.mapView.mapService;
      if (!svc || !this.appliedMassId) return [];
      return svc.dotTrailPoints[this.appliedMassId] || [];
    },
  },
  methods: {
    async applyFilter(options) {
      // Penanda SEBELUM await; ganti kapal dua kali cepat bikin dua applyFilter paralel.
      const seq = ++this.loadSeq;
      this.isLoading = true;
      await applyAssetSummaryFilter(this.$store, {
        dateRange: options.dateRange,
        interval: 'hour',
        massId: options.massId,
        summarySource: 'rollup',
        withSeries: true,
        seriesVariant: 'datalog',
      });
      if (seq !== this.loadSeq) return;

      this.ship = this.$store.getters['asset/assets'].find((asset) => asset.massId === options.massId);
      this.appliedDateRange = {
        start: new Date(options.dateRange.start.valueOf()),
        end: new Date(options.dateRange.end.valueOf()),
      };
      this.appliedMassId = options.massId;
      this.syncFilterUrl();
      this.isLoading = false;
    },
    toggleMenu() {
      this.sheetLevel = this.sheetLevel === 'closed' ? 'small' : 'closed';
    },
    setSheet(level) {
      this.sheetLevel = level;
    },
    // Ambang dihitung pas di-hover; trail sudah pasti kegambar di titik itu.
    onLegendHover(i) {
      if (i === null) {
        this.legendLabel = '';
        return;
      }
      const speeds = this.trailPoints.map((t) => t.gpsData?.speed ?? t.speed);
      const bounds = speedBounds(speeds).map((v) => kmhToKnot(v));
      if (bounds.length === 0) {
        this.legendLabel = 'No speed data';
        return;
      }
      // Jejak ini belum tentu pakai kelima warna; cari bucket milik chip ini.
      const bucket = bucketColors(speedBuckets(speeds)).indexOf(SPEED_COLORS[i]);
      this.legendLabel = bucket === -1 ? 'Not in this range' : `${bucketLabel(bucket, bounds)} kn`;
    },
    measureTopOffset() {
      const top = this.$el && this.$el.getBoundingClientRect ? this.$el.getBoundingClientRect().top : 0;
      if (Number.isFinite(top) && top > 0) this.topOffset = Math.round(top);
    },
    flyTo({ lat, lng }) {
      const svc = this.$refs.mapView && this.$refs.mapView.mapService;
      // Zoom 12: di atas itu citra satelit laut lepas balik hitam.
      if (svc && svc.map) svc.map.flyTo([lat, lng], 12);
    },
    readViewFromUrl() {
      const q = new URLSearchParams(window.location.search);
      const readNum = (key) => {
        const raw = q.get(key);
        return raw === null || raw === '' ? NaN : Number(raw);
      };
      const lat = readNum('lat');
      const lng = readNum('lng');
      const zoom = readNum('z');
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;
      return { lat, lng, zoom };
    },
    // Parameter lain digabung, bukan diganti: `menu` ada di sini.
    writeViewToUrl(lat, lng, zoom) {
      if (this._urlTimer) clearTimeout(this._urlTimer);
      this._urlTimer = setTimeout(() => {
        const q = new URLSearchParams(window.location.search);
        q.set('lat', Number(lat).toFixed(5));
        q.set('lng', Number(lng).toFixed(5));
        q.set('z', String(zoom));
        window.history.replaceState(null, '', `?${q.toString()}`);
      }, 500);
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
    if (this._urlTimer) clearTimeout(this._urlTimer);
  },
};
</script>

<style scoped>
.map-viewer-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.map-viewer-canvas {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

/* Melayang di atas panel yang bisa digulir, jadi butuh bayangan biar kebaca
   sebagai kontrol, bukan teks yang kepotong. */
.panel-toggle {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1101;
  transition: left 0.2s ease;
}

.panel-toggle-shifted {
  left: 432px;
}

/* Di HP panel selebar layar, jadi tombol di kiri-atas nutupin isi kartu.
   Digeser ke pojok bawah-kanan yang isinya lapang sekaligus dekat jempol. */
@media (max-width: 767px) {
  .panel-toggle {
    top: auto;
    left: auto;
    right: 16px;
    bottom: 16px;
  }
}
</style>
